import { useMemo } from 'react';
import type { AgentConnection, Job } from '@cef-ai/vault-sdk';
import { computeRecencyOrder } from '@/src/data/agent-order';

/**
 * Recency-ordered agents, STABLE across job polls. `jobs` is intentionally NOT
 * a dependency: a ~10s poll that only bumps `updatedAt` returns the very same
 * array reference, so blocks never reorder and the live widget iframe under
 * the user never remounts. Recomputes only when the agent set changes or when
 * jobs first load (`jobsLoaded` flips). `agents` is a stable state reference
 * (only a refetch replaces it), so keying on it does not churn on polls.
 * ponytail: freeze-until-agentset-changes; live re-rank behind a debounce only
 * if product wants feed-order-follows-activity.
 */
export function useStableAgentOrder(
  agents: AgentConnection[],
  jobs: Job[],
  jobsLoaded: boolean,
): AgentConnection[] {
  return useMemo(() => {
    const order = computeRecencyOrder(agents, jobsLoaded ? jobs : []);
    const byId = new Map(agents.map((a) => [a.agentId, a]));
    const ordered = order.map((id) => byId.get(id)).filter((a): a is AgentConnection => Boolean(a));
    // Any agent not in the computed order (edge race) trails at the end.
    for (const a of agents) if (!order.includes(a.agentId)) ordered.push(a);
    return ordered;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `jobs` omitted on purpose (freeze); see doc above.
  }, [agents, jobsLoaded]);
}
